import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type IntegrationProviderId,
  type IntegrationConnectBody,
  type IntegrationsListResponse,
  IntegrationsService,
  type IntegrationActivityEvent,
  type IntegrationActivityEventListQueryParams,
  type IntegrationSyncBody,
  type MeliOAuthStartResponse,
  type IntegrationConnectedAccountResponse,
} from 'src/api/generated';
import { type ApiResponse } from 'src/hooks/api/api';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';

export interface UseIntegrationsResponse extends ApiResponse {
  integrations: IntegrationsListResponse | null;
}

export interface UseIntegrationActivityEventsResponse extends ApiResponse {
  events: IntegrationActivityEvent[];
  nextPageToken?: string;
}

/** State returned while resolving the live account for an integration. */
export interface UseIntegrationConnectedAccountResponse extends ApiResponse {
  /** Live provider account, or null before it loads or when unavailable. */
  connectedAccount: IntegrationConnectedAccountResponse | null;
}

/**
 * Resolves the live external account authorized for an integration.
 *
 * @param providerId - Provider whose configured connection should be inspected.
 * @param enabled - Whether to issue the request; useful while disconnected.
 * @returns Query state plus the live account, cached per effective tenant as fresh for five minutes.
 */
export const useIntegrationConnectedAccount = (
  providerId: IntegrationProviderId,
  enabled = true
): UseIntegrationConnectedAccountResponse => {
  const { assumedTenantId } = useSupportTenant();
  const { user: currentUser } = useCurrentUser();
  const effectiveTenantId = assumedTenantId ?? currentUser?.tenantId ?? null;

  const { data, error, isLoading, refetch } =
    useQuery<IntegrationConnectedAccountResponse>({
      queryKey: ['integrations', providerId, 'account', effectiveTenantId],
      queryFn: () => IntegrationsService.getConnectedAccount(providerId),
      enabled: enabled && effectiveTenantId !== null,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    });

  return {
    connectedAccount: data ?? null,
    loading: isLoading,
    error,
    refetch,
  };
};

export const useIntegrations = (enabled = true): UseIntegrationsResponse => {
  const fetcher = async (): Promise<IntegrationsListResponse> => {
    return await IntegrationsService.listIntegrations();
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery<IntegrationsListResponse>({
    queryKey: ['integrations'],
    queryFn: fetcher,
    enabled,
  });

  return {
    loading,
    error,
    refetch,
    integrations: data ?? null,
  };
};

export const useIntegrationActivityEvents = (
  query: IntegrationActivityEventListQueryParams,
  enabled = true,
  refetchInterval: number | false = false
): UseIntegrationActivityEventsResponse => {
  const fetcher = async () =>
    await IntegrationsService.listActivityEvents(
      query.integrationConnectionId,
      query.provider,
      query.propertyId,
      query.propertyPortalPublicationId,
      query.externalSourceId,
      query.scope,
      query.status,
      query.type,
      query.dateFrom,
      query.dateTo,
      query.pageSize,
      query.pageToken
    );

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['integrations', 'activity-events', query],
    queryFn: fetcher,
    enabled,
    refetchInterval,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    loading,
    error,
    refetch,
    events: data?.items ?? [],
    nextPageToken: data?.nextPageToken,
  };
};

export const useConnectIntegration = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: {
    providerId: IntegrationProviderId;
    body: IntegrationConnectBody;
  }): Promise<void> => {
    await IntegrationsService.connectIntegration(
      params.providerId,
      params.body
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  return {
    connectIntegration: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

export const useDisconnectIntegration = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: {
    providerId: IntegrationProviderId;
  }): Promise<void> => {
    await IntegrationsService.disconnectIntegration(params.providerId);
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  return {
    disconnectIntegration: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

export const useEnqueueIntegrationSync = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: {
    providerId: IntegrationProviderId;
    body?: IntegrationSyncBody;
  }): Promise<void> => {
    await IntegrationsService.enqueueIntegrationSync(
      params.providerId,
      params.body
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  return {
    enqueueIntegrationSync: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

export const useStartMeliOAuth = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (): Promise<MeliOAuthStartResponse> => {
    return await IntegrationsService.startMeliOAuth();
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  return {
    startMeliOAuth: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};
